import { extractError } from "../utils/apiErrorUtils";
import { getToken } from "../users/service/localStorage";

// fetch (not axios) on purpose: FeedbackProvider is mounted app-wide, so this
// module gets pulled into many component test trees. Keeping it axios-free means
// those tests don't have to transform/mock axios just to render a menu. Mirrors
// analytics.js and lib/errorReporter.js.

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const authHeaders = () => {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { "x-auth-token": token } : {}),
  };
};

// Throw an axios-shaped error so extractError() behaves identically to the
// other axios-based API modules (reads response.data.error / .message).
const request = async (path, { method = "GET", body } = {}) => {
  let res;
  try {
    res = await fetch(`${apiUrl}${path}`, {
      method,
      headers: authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    throw { message: "Network error", code: "ERR_NETWORK", original: networkErr };
  }

  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    throw { response: { status: res.status, data } };
  }
  return data;
};

const toQuery = (params) => {
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return q ? `?${q}` : "";
};

export const submitFeedback = async ({ category, message, path }) => {
  try {
    const data = await request("/feedback", {
      method: "POST",
      body: { category, message, path, appVersion: process.env.REACT_APP_VERSION },
    });
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to send feedback"));
  }
};

export const adminGetFeedback = async ({ status, category, page = 1, limit = 25 } = {}) => {
  try {
    const data = await request(`/feedback${toQuery({ status, category, page, limit })}`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to load feedback"));
  }
};

export const adminUpdateFeedback = async (feedbackId, { status, adminNotes }) => {
  try {
    const body = {};
    if (status) body.status = status;
    if (adminNotes !== undefined) body.adminNotes = adminNotes;
    const data = await request(`/feedback/${feedbackId}`, { method: "PUT", body });
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to update feedback"));
  }
};
